/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  CreateClinicData,
  DeleteUserData,
  InviteUserData,
  LoginData,
  RefreshTokenData,
  RegisterClinicAdminData,
  RequestDynamicLinkData,
} from '../models';
import { Account, AdminPanelUsers, Clinic, User } from '../shared/types';

export interface IData {
  body: any;
}

export type LoginResponse = {
  token: string;
  refreshToken: string;
};

const SERVER_URL = process.env.REACT_APP_SERVER;

export const serverFetch = async (
  url: string,
  params: AxiosRequestConfig,
  data?: IData
): Promise<AxiosResponse> => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    Authorization: `Bearer ${token}`,
    ...params.headers,
  };

  const serverAddress = SERVER_URL;

  const requestParams = {
    ...params,
    url: `${serverAddress}${url}`,
    headers,
  };

  if (data?.body) {
    requestParams.data = JSON.stringify(data.body);
  }

  return axios(requestParams);
};

export const AuthApi = {
  setUpPortal: async (data: CreateClinicData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/admin/auth/createClinic`,
        {
          method: 'POST',
        },
        {
          body: data,
        }
      );
      return res.data;
    } catch (error) {
      const errorMsg = error.response.data.error.message;
      throw errorMsg;
    }
  },

  registerNewClinicAdmin: async (
    data: RegisterClinicAdminData
  ): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/admin/auth/register`,
        {
          method: 'POST',
        },
        {
          body: data,
        }
      );
      return res.data;
    } catch (error) {
      const errorMsg = error.response.data.error.message;
      throw errorMsg;
    }
  },

  login: async (data: LoginData): Promise<AxiosResponse<LoginResponse>> => {
    try {
      const res = await serverFetch(
        `/admin/auth/login`,
        {
          method: 'POST',
        },
        {
          body: data,
        }
      );
      return res.data;
    } catch (error) {
      const errorMsg = error.response.data.error.message;
      throw errorMsg;
    }
  },

  requestDynamicLink: async (
    data: RequestDynamicLinkData
  ): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/admin/auth/requestInviteLink`,
        {
          method: 'POST',
        },
        {
          body: data,
        }
      );
      return res.data;
    } catch {
      throw new Error('request dynamic link error');
    }
  },

  refreshToken: async (data: RefreshTokenData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/auth/refreshToken`,
        {
          method: 'POST',
        },
        {
          body: data,
        }
      );
      return res.data;
    } catch {
      throw new Error('refresh token error');
    }
  },
};

export const UsersApi = {
  fetchAllUsers: async (): Promise<AdminPanelUsers> => {
    const fetchPatients = serverFetch('/admin/patients', {
      method: 'get',
    });
    const fetchAdmins = serverFetch('/admin/admins', {
      method: 'get',
    });
    const fetchProviders = serverFetch('/admin/providers', {
      method: 'get',
    });
    try {
      const res = await axios.all([fetchPatients, fetchAdmins, fetchProviders]);
      return {
        patients: res[0].data.data,
        admins: res[1].data.data,
        careProviders: res[2].data.data,
      };
    } catch {
      throw new Error('fetch users error');
    }
  },

  editPatient: async (updatedPatient: User): Promise<AxiosResponse<User>> => {
    const { id, email, name, dateOfBirth } = updatedPatient;
    try {
      const res = await serverFetch(
        `/admin/patient/${id}`,
        {
          method: 'PUT',
        },
        {
          body: {
            email,
            name,
            dateOfBirth,
          },
        }
      );
      return res.data;
    } catch {
      throw new Error('edit patient error');
    }
  },

  editCareProvider: async (
    updatedCareProvider: User
  ): Promise<AxiosResponse<User>> => {
    const { id, email, name } = updatedCareProvider;
    try {
      const res = await serverFetch(
        `/admin/providers/${id}`,
        {
          method: 'PUT',
        },
        {
          body: {
            email,
            name,
          },
        }
      );
      return res.data;
    } catch {
      throw new Error('edit care provider error');
    }
  },

  editAdmin: async (updatedAdmin: User): Promise<AxiosResponse<User>> => {
    const { id, email, name } = updatedAdmin;
    try {
      const res = await serverFetch(
        `/admin/admins/${id}`,
        {
          method: 'PUT',
        },
        {
          body: {
            name,
            email,
          },
        }
      );
      return res.data;
    } catch {
      throw new Error('edit admin error');
    }
  },

  deleteCareProvider: async (data: DeleteUserData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(`/admin/providers/${data.userId}`, {
        method: 'DELETE',
      });
      return res.data;
    } catch {
      throw new Error('delete care provider error');
    }
  },

  deleteAdmin: async (data: DeleteUserData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(`/admin/admins/${data.userId}`, {
        method: 'DELETE',
      });
      return res.data;
    } catch {
      throw new Error('delete admin error');
    }
  },

  invitePatient: async (data: InviteUserData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/admin/patient/invite`,
        {
          method: 'POST',
        },
        {
          body: {
            email: data.email,
          },
        }
      );
      return res.data;
    } catch {
      throw new Error('invite patient error');
    }
  },

  inviteCareProvider: async (data: InviteUserData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/admin/provider/invite`,
        {
          method: 'POST',
        },
        {
          body: {
            email: data.email,
          },
        }
      );
      return res.data;
    } catch {
      throw new Error('invite care provider error');
    }
  },

  inviteAdmin: async (data: InviteUserData): Promise<AxiosResponse> => {
    try {
      const res = await serverFetch(
        `/admin/admin/invite`,
        {
          method: 'POST',
        },
        {
          body: {
            email: data.email,
          },
        }
      );
      return res.data;
    } catch {
      throw new Error('invite admin error');
    }
  },
  fetchMyAccount: async (): Promise<Account> => {
    try {
      const res = await serverFetch(`/admin/accounts/me`, {
        method: 'GET',
      });
      return res.data.data;
    } catch {
      throw new Error('fetch my account error');
    }
  },
};

export const ClinicsApi = {
  fetchClinics: async (): Promise<Clinic[]> => {
    try {
      const res = await serverFetch(`/clinics`, {
        method: 'GET',
      });
      return res.data.data;
    } catch {
      throw new Error('fetch clinics error');
    }
  },
};
